// Framing for the small county map on a county page: which part of the shared 960x600
// map to show, and how big to draw its marks. Pure, so it can be tested without a DOM.

import type { Bounds } from "./map/view";

export interface Frame {
  /** SVG viewBox string, "x y w h". */
  viewBox: string;
  /** Width over height, for an aspect-ratio box that holds its space before the map loads. */
  aspect: number;
  /** One screen pixel in map units when the map is drawn `widthPx` wide. */
  unit: number;
}

/**
 * Frame a state with a margin, never narrower than 3:4 or wider than 16:9, so a tall
 * state (Idaho) and a wide one (Tennessee) both sit in a box that fits the column.
 */
export function frameFor(bounds: Bounds, widthPx = 320, pad = 0.05): Frame {
  const [x0, y0, x1, y1] = bounds;
  let w = Math.max(x1 - x0, 1);
  let h = Math.max(y1 - y0, 1);
  const cx = (x0 + x1) / 2;
  const cy = (y0 + y1) / 2;
  w *= 1 + pad * 2;
  h *= 1 + pad * 2;
  const minAspect = 3 / 4;
  const maxAspect = 16 / 9;
  if (w / h < minAspect) w = h * minAspect;
  if (w / h > maxAspect) h = w / maxAspect;
  const x = cx - w / 2;
  const y = cy - h / 2;
  const r = (n: number) => Math.round(n * 100) / 100;
  return {
    viewBox: `${r(x)} ${r(y)} ${r(w)} ${r(h)}`,
    aspect: r(w / h),
    unit: w / widthPx,
  };
}
